import { useEffect, useState } from "react";

export const breakpoints = {
    s: 744,
    m: 1024,
    l: 1280,
    xl: 1440,
} as const;

export const defaultMediaQueryValues = {
    isMobile: false,
    isTablet: false,
    isTabletLarge: false,
    isDesktop: false,
    isDesktopLarge: false,

    isBelowTablet: false,
    isBelowTabletLarge: false,
    isBelowDesktop: false,
    isBelowDesktopLarge: false,

    isAboveTablet: false,
    isAboveTabletLarge: false,
    isAboveDesktop: false,
    isAboveDesktopLarge: false,

    isPortrait: false,
    isLandscape: false,
};

/**
 * Create verbose type here to enable JSDocs for easy usage. Hover over property to find out sizing when using the useMediaQuery hook.
 *
 * Represents the current state of all media queries.
 * Each property indicates whether a specific breakpoint or orientation is currently active.
 */
export type MediaQueriesReturn = {
    /** Viewport is less than 744px */
    isMobile: boolean;

    /** Viewport is between 744px and 1023px */
    isTablet: boolean;

    /** Viewport is between 1024px and 1279px */
    isTabletLarge: boolean;

    /** Viewport is between 1280px and 1439px */
    isDesktop: boolean;

    /** Viewport is 1440px or wider */
    isDesktopLarge: boolean;

    /** Viewport is less than 744px */
    isBelowTablet: boolean;

    /** Viewport is less than 1024px */
    isBelowTabletLarge: boolean;

    /** Viewport is less than 1280px */
    isBelowDesktop: boolean;

    /** Viewport is less than 1440px */
    isBelowDesktopLarge: boolean;

    /** Viewport is 744px or wider */
    isAboveTablet: boolean;

    /** Viewport is 1024px or wider */
    isAboveTabletLarge: boolean;

    /** Viewport is 1280px or wider */
    isAboveDesktop: boolean;

    /** Viewport is 1440px or wider */
    isAboveDesktopLarge: boolean;

    /** Viewport orientation is portrait */
    isPortrait: boolean;

    /** Viewport orientation is landscape */
    isLandscape: boolean;
};

const useMediaQuery = (): MediaQueriesReturn => {
    const [matches, setMatches] = useState<MediaQueriesReturn>(defaultMediaQueryValues);

    useEffect(() => {
        // Create all queries to check sizing
        const mediaQueryLists: Record<keyof MediaQueriesReturn, MediaQueryList> = {
            isMobile: window.matchMedia(`(max-width: ${breakpoints.s - 1}px)`),
            isTablet: window.matchMedia(
                `(min-width: ${breakpoints.s}px) and (max-width: ${breakpoints.m - 1})`
            ),
            isTabletLarge: window.matchMedia(
                `(min-width: ${breakpoints.m}px) and (max-width: ${breakpoints.l - 1})`
            ),
            isDesktop: window.matchMedia(
                `(min-width: ${breakpoints.l}px) and (max-width: ${breakpoints.xl - 1})`
            ),
            isDesktopLarge: window.matchMedia(`(min-width: ${breakpoints.xl}px)`),

            isBelowTablet: window.matchMedia(`(max-width: ${breakpoints.s - 1}px)`),
            isBelowTabletLarge: window.matchMedia(`(max-width: ${breakpoints.m - 1}px)`),
            isBelowDesktop: window.matchMedia(`(max-width: ${breakpoints.l - 1}px)`),
            isBelowDesktopLarge: window.matchMedia(`(max-width: ${breakpoints.xl - 1}px)`),

            isAboveTablet: window.matchMedia(`(min-width: ${breakpoints.s}px)`),
            isAboveTabletLarge: window.matchMedia(`(min-width: ${breakpoints.m}px)`),
            isAboveDesktop: window.matchMedia(`(min-width: ${breakpoints.l}px)`),
            isAboveDesktopLarge: window.matchMedia(`(min-width: ${breakpoints.xl}px)`),

            isPortrait: window.matchMedia(`(orientation: portrait)`),
            isLandscape: window.matchMedia(`(orientation: landscape)`),
        };

        // Turn all of the media queries into a boolean by calling .matches
        const getValues = (): MediaQueriesReturn => {
            const matchlist: MediaQueriesReturn = { ...defaultMediaQueryValues };

            (
                Object.entries(mediaQueryLists) as [keyof MediaQueriesReturn, MediaQueryList][]
            ).forEach(([key, query]) => (matchlist[key] = query.matches));

            return matchlist as MediaQueriesReturn;
        };

        const handleChange = () => setMatches(getValues());

        // Add listeners
        Object.values(mediaQueryLists).forEach((mql) => {
            mql.addEventListener("change", handleChange);
        });

        // Initial check
        setMatches(getValues());

        return () => {
            // Cleanup listeners
            Object.values(mediaQueryLists).forEach((mql) => {
                mql.removeEventListener("change", handleChange);
            });
        };
    }, []);

    return matches;
};

export default useMediaQuery;
